import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faHandshake,
  faTag,
  faMoneyBillWave,
  faKey,
  faFileSignature,
  faGift,
  faCircleCheck,
  faStar,
  faIdBadge,
  faPercent,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Icon resolution — a small keyword lookup so the icon slot can stay as a
// simple text token and still render a real FA icon.
//
// Note: kept as an inline switch (not a module-level `const` object) so the
// schema-linter's static-extraction eval doesn't trip on the FA imports.
// ============================================================================

function resolveIcon(name?: string | null): IconDefinition {
  const key = (name || '').trim().toLowerCase();
  switch (key) {
    case 'handshake': return faHandshake;
    case 'tag':
    case 'discount': return faTag;
    case 'percent': return faPercent;
    case 'money': return faMoneyBillWave;
    case 'key': return faKey;
    case 'contract':
    case 'signature': return faFileSignature;
    case 'gift': return faGift;
    case 'check': return faCircleCheck;
    case 'badge':
    case 'id': return faIdBadge;
    case 'shield':
    case 'savings': return faShieldHalved;
    case 'star':
    default: return faStar;
  }
}

// ============================================================================
// Slot group definitions (module-level literals — extractor follows one level)
// ============================================================================

const benefit1Def: SlotGroupDefinition = {
  label: 'Benefit 1',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      description:
        'Icon name (choose from: handshake, tag, percent, money, key, contract, gift, check, star, badge, shield).',
      defaultValue: 'tag',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Waived Application Fee',
    },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: '($60 Value)',
    },
  },
};

const benefit2Def: SlotGroupDefinition = {
  label: 'Benefit 2',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      description:
        'Icon name (choose from: handshake, tag, percent, money, key, contract, gift, check, star, badge, shield).',
      defaultValue: 'money',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Waived Administration Fee',
    },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: '($200 Value)',
    },
  },
};

const benefit3Def: SlotGroupDefinition = {
  label: 'Benefit 3',
  fields: {
    icon: {
      label: 'Icon',
      type: 'text',
      description:
        'Icon name (choose from: handshake, tag, percent, money, key, contract, gift, check, star, badge, shield).',
      defaultValue: 'shield',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: 'Total Savings',
    },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: '$260!',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

interface BenefitData {
  icon?: string | null;
  title?: string | null;
  description?: string | null;
}

function benefitBody(data: BenefitData, index: number) {
  const icon = resolveIcon(data.icon);
  return (
    <article
      className="pep-benefit-card"
      data-reveal
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <span className="pep-benefit-icon" aria-hidden="true">
        <FontAwesomeIcon icon={icon} />
      </span>
      <h3 className="pep-benefit-title">{data.title || 'Program Benefit'}</h3>
      <p className="pep-benefit-value">{data.description || ''}</p>
    </article>
  );
}

export function PreferredEmployerBenefits() {
  const benefit1 = useSlotGroup('preferredemployerbenefits.benefit_1', benefit1Def);
  const benefit2 = useSlotGroup('preferredemployerbenefits.benefit_2', benefit2Def);
  const benefit3 = useSlotGroup('preferredemployerbenefits.benefit_3', benefit3Def);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 90 });

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pep-benefits-header">
          <RichTextContent
            path="preferredemployerbenefits.eyebrow"
            as="span"
            className="pep-benefits-eyebrow"
          />
          <RichTextContent
            path="preferredemployerbenefits.heading"
            as="h2"
            className="pep-benefits-heading"
          />
          <RichTextContent
            path="preferredemployerbenefits.intro"
            as="div"
            className="pep-benefits-intro"
          />
        </div>

        <div ref={gridRef} className="pep-benefits-grid">
          <EditableSlotGroup
            groupPath="preferredemployerbenefits.benefit_1"
            definition={benefit1Def}
            className="pep-benefit-cell"
          >
            {benefitBody(benefit1, 0)}
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="preferredemployerbenefits.benefit_2"
            definition={benefit2Def}
            className="pep-benefit-cell"
          >
            {benefitBody(benefit2, 1)}
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="preferredemployerbenefits.benefit_3"
            definition={benefit3Def}
            className="pep-benefit-cell"
          >
            {benefitBody(benefit3, 2)}
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .pep-benefits-header {
          text-align: center;
          max-width: 42rem;
          margin: 0 auto 3rem;
        }

        .pep-benefits-eyebrow {
          display: block;
          font-family: var(--vibe-font-heading);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.25em;
          color: var(--vibe-color-primary);
          margin-bottom: 0.75rem;
        }
        .pep-benefits-eyebrow p,
        .pep-benefits-eyebrow span {
          font: inherit;
          color: inherit;
          letter-spacing: inherit;
          text-transform: inherit;
          margin: 0;
        }

        .pep-benefits-heading {
          font-family: var(--vibe-font-heading);
          font-size: 2rem;
          font-weight: 700;
          color: var(--vibe-color-text);
          line-height: 1.15;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin: 0 0 1.25rem;
        }
        @media (min-width: 768px) {
          .pep-benefits-heading {
            font-size: 2.5rem;
          }
        }
        .pep-benefits-heading p,
        .pep-benefits-heading span {
          font: inherit;
          color: inherit;
          margin: 0;
        }

        .pep-benefits-intro {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
          text-wrap: balance;
        }
        @media (min-width: 768px) {
          .pep-benefits-intro { font-size: 1.0625rem; }
        }
        .pep-benefits-intro p { margin: 0 0 0.75rem; }
        .pep-benefits-intro p:last-child { margin-bottom: 0; }
        .pep-benefits-intro a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .pep-benefits-grid {
          display: grid;
          grid-template-columns: 1fr;
          grid-auto-rows: 1fr;
          gap: 1.25rem;
          max-width: 60rem;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .pep-benefits-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 1.5rem;
          }
        }

        .pep-benefit-cell {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .pep-benefit-cell > * {
          flex: 1 1 auto;
        }

        .pep-benefit-card {
          height: 100%;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0;
          padding: 2rem 1.75rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s, border-color 0.25s;
        }
        .pep-benefit-card:hover {
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 30%, #e8e8ea);
        }

        .pep-benefit-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
          font-size: 1.25rem;
          margin-bottom: 1.5rem;
        }

        .pep-benefit-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 600;
          line-height: 1.3;
          color: var(--vibe-color-text);
          margin: 0 0 0.5rem;
        }

        .pep-benefit-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0;
        }

        @media (prefers-reduced-motion: reduce) {
          .pep-benefit-card {
            transition: none;
          }
          .pep-benefit-card:hover {
            transform: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerBenefits',
  displayName: 'Preferred Employer — Benefits',
  tokens: {
    'preferredemployerbenefits.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Preferred Employer Program</p>',
    },
    'preferredemployerbenefits.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Exclusive Perks for Preferred Employers</p>',
    },
    'preferredemployerbenefits.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue:
        '<p>Employees of our preferred organizations receive move-in savings and priority scheduling when making <span data-type="field-token" data-field="property_name"></span> home.</p>',
    },
  },
  slotGroups: {
    'preferredemployerbenefits.benefit_1': benefit1Def,
    'preferredemployerbenefits.benefit_2': benefit2Def,
    'preferredemployerbenefits.benefit_3': benefit3Def,
  },
};

(PreferredEmployerBenefits as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerBenefits;
